import { Matrix4, Quaternion, Vector3 } from 'three';
import { PHYSICS, RIDE_TUNING } from '../config.js';
import type { Ride, RideTuning } from '../config.js';
import { MODE } from '../protocol.js';
import type { RiderMode } from '../protocol.js';
import { TRICK_KIND, TRICKS, encodeTrick } from '../tricks.js';
import { advanceRail, railPoint, railTangent } from '../collision.js';
import type { CollisionWorld, Contact, RailCursor, RailNet } from '../collision.js';
import { SURFACE } from '../course/builder.js';
import type { Rail, V3 } from '../course/builder.js';

export interface SimInput {
  /** -1 = left, 1 = right. */
  steer: number;
  /** Push / pedal; also "hold forward" to fly out of a vert lip. */
  push: boolean;
  brake: boolean;
  /** Jump held (crouch/charge); releasing ollies. */
  jump: boolean;
  /** Flip trick pressed this frame. */
  flip: boolean;
  /** Grab held. */
  grab: boolean;
}

export const NO_INPUT: SimInput = {
  steer: 0,
  push: false,
  brake: false,
  jump: false,
  flip: false,
  grab: false,
};

export type SimEvent =
  | { type: 'ollie'; charge: number }
  | { type: 'land'; airTime: number; tricks: string[]; impact: number }
  | { type: 'grindStart'; kind: Rail['kind'] }
  | { type: 'grindEnd' }
  | { type: 'bail'; reason: 'landing' | 'balance' | 'wall' | 'knockout' }
  | { type: 'recover' }
  | { type: 'trick'; name: string }
  | { type: 'wallHit'; speed: number };

type Mode = 'ground' | 'air' | 'grind' | 'bail';

interface VertState {
  /** Horizontal unit normal pointing away from the wall (into the bowl). */
  normal: Vector3;
  lipY: number;
  turnRate: number;
  turned: number;
}

interface FlipState {
  variant: number;
  t: number;
  duration: number;
}

const WORLD_UP = new Vector3(0, 1, 0);
const tmpA = new Vector3();
const tmpB = new Vector3();
const tmpC = new Vector3();
const tmpQ = new Quaternion();
const tmpM = new Matrix4();
const contacts: Contact[] = [];

function toV3(v: Vector3): V3 {
  return [v.x, v.y, v.z];
}

function fromV3(target: Vector3, v: V3): Vector3 {
  return target.set(v[0], v[1], v[2]);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Rotates `v` by the minimal rotation taking unit vector `from` to unit vector `to`. */
function rotateBetween(v: Vector3, from: Vector3, to: Vector3): void {
  if (from.dot(to) > 0.999999) return;
  tmpQ.setFromUnitVectors(from, to);
  v.applyQuaternion(tmpQ);
}

function directionVariant(steer: number, push: boolean, brake: boolean): number {
  if (steer < -0.3) return 1;
  if (steer > 0.3) return 2;
  if (push || brake) return 3;
  return 0;
}

/**
 * Local rider controller: surface-following ground movement over arbitrary transitions,
 * vert airs that return to the wall unless forward is held, rail grinds with balance,
 * and bails. Deterministic apart from the balance noise source.
 */
export class RiderSim {
  public mode: Mode = 'ground';
  public readonly pos = new Vector3();
  public readonly vel = new Vector3();
  /** Surface normal on the ground; body up in the air. */
  public readonly up = new Vector3(0, 1, 0);
  /** Heading (ground) or body forward (air). */
  public readonly fwd = new Vector3(0, 0, 1);
  public readonly quaternion = new Quaternion();
  public ride: Ride;
  public tuning: RideTuning;

  public charge = 0;
  public airTime = 0;
  public spin = 0;
  public vert: VertState | null = null;
  public flip: FlipState | null = null;
  public grabVariant = -1;
  public grabTime = 0;
  public pushing = false;
  public braking = false;
  public pumping = false;
  public groundFlags = 0;

  public rail: Rail | null = null;
  public readonly railCursor: RailCursor = { seg: 0, t: 0 };
  public railDir = 1;
  public railSpeed = 0;
  public balance = 0;
  public balanceVel = 0;
  public grindTime = 0;
  public grindVariant = 0;

  public bailTime = 0;
  public bailLocked = false;
  public readonly tumbleAxis = new Vector3(1, 0, 0);
  public tumble = 0;

  private accumulator = 0;
  private prevJump = false;
  private coyote = 0;
  private jumpBuffer = 0;
  private railCooldown = 0;
  private lastRail: Rail | null = null;
  private fakieTime = 0;
  private spinTotal = 0;
  private tricks: string[] = [];
  private trickCooldown = 0;
  private readonly events: SimEvent[] = [];
  private readonly prev = new Vector3();

  public constructor(
    private readonly world: CollisionWorld,
    private readonly rails: RailNet,
    ride: Ride,
    private readonly random: () => number = Math.random,
  ) {
    this.ride = ride;
    this.tuning = RIDE_TUNING[ride];
  }

  public setRide(ride: Ride): void {
    this.ride = ride;
    this.tuning = RIDE_TUNING[ride];
  }

  public teleport(p: V3, yaw: number): void {
    fromV3(this.pos, p);
    this.vel.set(0, 0, 0);
    this.up.set(0, 1, 0);
    this.fwd.set(Math.sin(yaw), 0, Math.cos(yaw));
    this.mode = 'ground';
    this.vert = null;
    this.flip = null;
    this.grabVariant = -1;
    this.rail = null;
    this.bailLocked = false;
    this.tumble = 0;
    this.charge = 0;
    this.accumulator = 0;
    const hit = this.world.raycast([p[0], p[1] + 2, p[2]], [0, -1, 0], 6);
    if (hit) {
      fromV3(this.pos, hit.point);
      fromV3(this.up, hit.normal);
    } else {
      this.mode = 'air';
    }
    this.updateQuaternion();
  }

  /** Advances the simulation by a frame; returns events raised during it. */
  public update(frameDt: number, input: SimInput): SimEvent[] {
    this.events.length = 0;
    const dt = PHYSICS.fixedStep;
    this.accumulator = Math.min(this.accumulator + frameDt, PHYSICS.maxFrameDelta);
    let first = true;
    while (this.accumulator >= dt) {
      this.accumulator -= dt;
      // Edge-triggered inputs apply to the first substep only.
      this.step(dt, first ? input : { ...input, flip: false });
      first = false;
    }
    this.updateQuaternion();
    return this.events;
  }

  /** Knockback from a server-confirmed hit. */
  public applyHit(kb: V3, shock: number, knockout: boolean): void {
    fromV3(tmpA, kb);
    if (this.mode === 'grind') {
      const side = this.random() < 0.5 ? -1 : 1;
      this.balance += side * shock * 0.6;
      this.balanceVel += side * shock * 2.5;
      if (knockout) this.bail('knockout', true);
      return;
    }
    this.vel.add(tmpA);
    if (this.mode === 'ground' && tmpA.y > 1) this.enterAir(false);
    if (knockout) this.bail('knockout', true);
  }

  public releaseKnockout(): void {
    this.bailLocked = false;
  }

  public get speed(): number {
    return this.vel.length();
  }

  public get wireMode(): RiderMode {
    switch (this.mode) {
      case 'ground':
        return MODE.ground;
      case 'air':
        return MODE.air;
      case 'grind':
        return MODE.grind;
      default:
        return this.bailLocked ? MODE.down : MODE.bail;
    }
  }

  /** Trick code and animation scalar for the wire. */
  public get trickCode(): { k: number; a: number } {
    if (this.mode === 'grind') {
      return { k: encodeTrick(TRICK_KIND.grind, this.grindVariant), a: clamp(this.balance, -1, 1) };
    }
    if (this.flip) {
      return { k: encodeTrick(TRICK_KIND.flip, this.flip.variant), a: this.flip.t };
    }
    if (this.grabVariant >= 0) {
      return {
        k: encodeTrick(TRICK_KIND.grab, this.grabVariant),
        a: Math.min(1, this.grabTime * 6),
      };
    }
    return { k: 0, a: 0 };
  }

  private emit(event: SimEvent): void {
    this.events.push(event);
  }

  private step(dt: number, input: SimInput): void {
    this.lastInput = input;
    const released = this.prevJump && !input.jump;
    this.prevJump = input.jump;
    if (released) this.jumpBuffer = PHYSICS.jumpBufferSeconds;
    else this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);
    this.railCooldown = Math.max(0, this.railCooldown - dt);
    this.trickCooldown = Math.max(0, this.trickCooldown - dt);

    switch (this.mode) {
      case 'ground':
        this.stepGround(dt, input);
        break;
      case 'air':
        this.stepAir(dt, input);
        break;
      case 'grind':
        this.stepGrind(dt, input);
        break;
      case 'bail':
        this.stepBail(dt);
        break;
    }
    if (this.pos.y < -40 || !Number.isFinite(this.pos.x + this.pos.y + this.pos.z)) {
      this.teleport([0, 0, 26], Math.PI);
    }
  }

  // ---------------------------------------------------------------- ground

  private stepGround(dt: number, input: SimInput): void {
    const n = this.up;
    const t = this.tuning;
    this.fwd.projectOnPlane(n);
    if (this.fwd.lengthSq() < 1e-6) this.fwd.copy(this.vel).projectOnPlane(n);
    if (this.fwd.lengthSq() < 1e-6) this.fwd.set(0, 0, 1).projectOnPlane(n);
    this.fwd.normalize();

    const speed = this.vel.length();
    const turnRate =
      (t.turnRate + (t.turnRateFast - t.turnRate) * clamp(speed / 22, 0, 1)) *
      (input.brake ? 1.35 : 1);
    const angle = -input.steer * turnRate * dt;
    if (angle !== 0) {
      this.fwd.applyAxisAngle(n, angle);
      this.vel.applyAxisAngle(n, angle);
    }
    // Carve: bleed sideways slip, drop any normal component.
    const vf = this.vel.dot(this.fwd);
    tmpA.copy(this.vel).addScaledVector(this.fwd, -vf).projectOnPlane(n);
    tmpA.multiplyScalar(Math.exp(-PHYSICS.lateralGrip * dt));
    this.vel.copy(this.fwd).multiplyScalar(vf).add(tmpA);

    // Gravity along the surface, softened on transitions so riders reach lips.
    const onTransition = (this.groundFlags & SURFACE.transition) !== 0;
    const gravity = PHYSICS.gravity * (onTransition ? PHYSICS.transitionGravityScale : 1);
    this.vel.addScaledVector(tmpB.set(0, -gravity, 0).projectOnPlane(n), dt);

    this.pushing = false;
    if (input.push && n.y > 0.75 && vf < t.pushSpeed && !input.jump) {
      this.vel.addScaledVector(this.fwd, t.pushAccel * dt * (vf < 0 ? 1.6 : 1));
      this.pushing = true;
    }
    this.pumping =
      input.jump &&
      (this.groundFlags & SURFACE.transition) !== 0 &&
      n.y < 0.985 &&
      this.vel.y < -0.4;
    if (this.pumping && speed > 0.5) {
      this.vel.addScaledVector(tmpC.copy(this.vel).normalize(), PHYSICS.pumpAccel * dt);
    }
    this.braking = input.brake && speed > 0.3;
    if (this.braking) this.decelerate(PHYSICS.brakeDecel * dt);
    this.decelerate((t.rollingDrag + PHYSICS.airDrag * speed * speed) * dt);
    if (this.vel.length() > PHYSICS.maxSpeed) this.vel.setLength(PHYSICS.maxSpeed);

    // Rolling backwards on flat ground: revert to face travel.
    if (vf < -1 && n.y > 0.85) {
      this.fakieTime += dt;
      if (this.fakieTime > 0.35) {
        this.fwd.negate();
        this.fakieTime = 0;
      }
    } else {
      this.fakieTime = 0;
    }

    // Crouch/charge, then ollie on release (or buffered release).
    if (input.jump) this.charge = Math.min(1, this.charge + dt / PHYSICS.chargeSeconds);
    if (this.jumpBuffer > 0) {
      this.jumpBuffer = 0;
      this.ollie(n);
      return;
    }
    if (!input.jump) this.charge = Math.max(0, this.charge - dt * 4);

    this.prev.copy(this.pos);
    this.pos.addScaledVector(this.vel, dt);
    this.resolveWalls(true);
    this.followGround(dt);
  }

  private decelerate(amount: number): void {
    const speed = this.vel.length();
    if (speed <= amount) this.vel.set(0, 0, 0);
    else this.vel.multiplyScalar((speed - amount) / speed);
  }

  private attachable(normal: V3, flags: number, reference: Vector3): boolean {
    const alignment = normal[0] * reference.x + normal[1] * reference.y + normal[2] * reference.z;
    if (alignment < 0.45) return false;
    return (flags & SURFACE.transition) !== 0 || normal[1] > 0.6;
  }

  /** Sticks to the surface below, rotating momentum across transitions or launching. */
  private followGround(dt: number): void {
    const n = this.up;
    const lift = PHYSICS.probeLift;
    const origin = tmpA.copy(this.pos).addScaledVector(n, lift);
    const hit = this.world.raycast(toV3(origin), [-n.x, -n.y, -n.z], lift + PHYSICS.snapDown);
    if (!hit || !this.attachable(hit.normal, hit.flags, n)) {
      this.enterAir(true);
      return;
    }
    const gap = hit.t - lift;
    if (gap < -PHYSICS.stepUp) return;
    const gravityDrop = 0.5 * Math.abs(PHYSICS.gravity * n.y) * dt * dt;
    if (gap > Math.max(PHYSICS.launchGap, gravityDrop) && this.vel.lengthSq() > 4) {
      this.enterAir(true);
      return;
    }
    fromV3(this.pos, hit.point);
    fromV3(tmpB, hit.normal);
    rotateBetween(this.vel, n, tmpB);
    rotateBetween(this.fwd, n, tmpB);
    this.up.copy(tmpB);
    this.vel.projectOnPlane(this.up);
    this.groundFlags = hit.flags;
    this.coyote = PHYSICS.coyoteSeconds;
  }

  public get grounded(): boolean {
    return this.mode === 'ground';
  }

  private ollie(n: Vector3): void {
    const t = this.tuning;
    const power = t.ollie + (t.chargedOllie - t.ollie) * this.charge;
    tmpA.copy(n).multiplyScalar(0.75).addScaledVector(WORLD_UP, 0.25).normalize();
    this.vel.addScaledVector(tmpA, power);
    this.emit({ type: 'ollie', charge: this.charge });
    this.charge = 0;
    this.enterAir(false);
    this.coyote = 0;
  }

  /** Leaves the ground. `rolledOff` means the surface ended (lip, edge, crest). */
  private enterAir(rolledOff: boolean, keepCombo = false): void {
    const n = this.up;
    this.mode = 'air';
    this.airTime = 0;
    this.spin = 0;
    this.spinTotal = 0;
    if (!keepCombo) this.tricks = [];
    this.pushing = false;
    this.braking = false;
    this.pumping = false;
    this.flip = null;
    this.grabVariant = -1;
    this.grabTime = 0;
    this.vert = null;
    if (!rolledOff) this.coyote = 0;
    const transition = (this.groundFlags & SURFACE.transition) !== 0;
    if (rolledOff && transition && n.y < PHYSICS.vertNormalY && this.vel.y > 0.5) {
      const wall = new Vector3(n.x, 0, n.z);
      if (wall.lengthSq() > 1e-6) {
        wall.normalize();
        const contain = (this.groundFlags & SURFACE.contain) !== 0;
        const forward = this.lastInput.push && !contain;
        if (forward) {
          this.vel.addScaledVector(
            wall,
            -(PHYSICS.vertExitBoost + this.vel.length() * PHYSICS.vertExitSpeedScale),
          );
        } else {
          const outward = -this.vel.dot(wall);
          if (outward > 0) this.vel.addScaledVector(wall, outward);
          const airtime = Math.max(0.35, (2 * this.vel.y) / PHYSICS.gravity);
          this.vert = { normal: wall, lipY: this.pos.y, turnRate: Math.PI / airtime, turned: 0 };
        }
      }
    }
    // Body forward follows travel direction when launching.
    if (this.vel.lengthSq() > 1) {
      tmpA.copy(this.vel).normalize();
      if (Math.abs(tmpA.dot(this.up)) < 0.95)
        this.fwd.copy(tmpA).projectOnPlane(this.up).normalize();
    }
  }

  // ---------------------------------------------------------------- air

  private lastInput: SimInput = NO_INPUT;

  private stepAir(dt: number, input: SimInput): void {
    this.lastInput = input;
    const t = this.tuning;
    this.airTime += dt;
    this.coyote = Math.max(0, this.coyote - dt);
    if (this.jumpBuffer > 0 && this.coyote > 0 && this.vel.y < 2) {
      this.jumpBuffer = 0;
      this.mode = 'ground';
      this.ollie(WORLD_UP);
      return;
    }
    if (input.jump) this.charge = Math.min(1, this.charge + dt / PHYSICS.chargeSeconds);

    this.vel.y -= PHYSICS.gravity * dt;
    const speed = this.vel.length();
    this.decelerate(PHYSICS.airDrag * speed * speed * dt);

    // Spin about the body's up axis; vert airs also auto-turn to face back down the wall.
    const spinDelta = -input.steer * t.spinRate * dt;
    this.spin += spinDelta;
    this.spinTotal += Math.abs(spinDelta);
    if (spinDelta !== 0) this.fwd.applyAxisAngle(this.up, spinDelta);
    if (this.vert) {
      const turn = Math.min(Math.PI - this.vert.turned, this.vert.turnRate * dt);
      if (turn > 0) {
        this.fwd.applyAxisAngle(this.up, turn);
        this.vert.turned += turn;
      }
      this.up
        .lerp(tmpA.copy(this.vert.normal).multiplyScalar(0.6).add(WORLD_UP), 2 * dt)
        .normalize();
    } else {
      this.up.lerp(WORLD_UP, Math.min(1, 3.5 * dt)).normalize();
    }
    this.fwd.projectOnPlane(this.up);
    if (this.fwd.lengthSq() < 1e-6) this.fwd.set(0, 0, 1).projectOnPlane(this.up);
    this.fwd.normalize();

    this.updateTricks(dt, input);

    this.prev.copy(this.pos);
    this.pos.addScaledVector(this.vel, dt);

    if (this.vert) {
      if (this.holdVert(dt)) return;
    }
    if (this.tryRails()) return;
    if (this.tryLand()) return;
    this.resolveWalls(false);
  }

  private updateTricks(dt: number, input: SimInput): void {
    const t = this.tuning;
    if (this.flip) {
      this.flip.t = Math.min(1, this.flip.t + dt / this.flip.duration);
      if (this.flip.t >= 1) {
        this.tricks.push(TRICKS[this.ride].flips[this.flip.variant] ?? 'Flip');
        this.flip = null;
        this.trickCooldown = PHYSICS.trickCooldownSeconds;
      }
    } else if (input.flip && this.trickCooldown <= 0 && this.airTime > 0.05) {
      const variant = directionVariant(input.steer, input.push, input.brake);
      this.flip = { variant, t: 0, duration: t.flipTime * (variant === 2 ? 1.2 : 1) };
    }
    if (input.grab && !this.flip) {
      if (this.grabVariant < 0) {
        this.grabVariant = directionVariant(input.steer, input.push, input.brake);
        this.grabTime = 0;
      }
      this.grabTime += dt;
    } else if (this.grabVariant >= 0) {
      if (this.grabTime > 0.25)
        this.tricks.push(TRICKS[this.ride].grabs[this.grabVariant] ?? 'Grab');
      this.grabVariant = -1;
      this.grabTime = 0;
    }
  }

  /** Keeps a returning vert air hovering just inside the lip and re-attaches to the wall. */
  private holdVert(dt: number): boolean {
    const vert = this.vert!;
    const probe: V3 = [
      this.pos.x + vert.normal.x * 1.2,
      vert.lipY - 0.45,
      this.pos.z + vert.normal.z * 1.2,
    ];
    const hit = this.world.raycast(probe, [-vert.normal.x, 0, -vert.normal.z], 3);
    if (!hit || (hit.flags & SURFACE.transition) === 0) {
      this.vert = null;
      return false;
    }
    const wall = tmpA.set(hit.normal[0], 0, hit.normal[2]);
    if (wall.lengthSq() > 1e-6) vert.normal.copy(wall.normalize());
    const offset =
      (this.pos.x - hit.point[0]) * vert.normal.x + (this.pos.z - hit.point[2]) * vert.normal.z;
    const correction = (PHYSICS.vertInset - offset) * Math.min(1, 12 * dt);
    this.pos.addScaledVector(vert.normal, correction);
    const along = this.vel.dot(vert.normal);
    this.vel.addScaledVector(vert.normal, -along);

    if (this.vel.y < 0 && this.pos.y < vert.lipY - 0.05) {
      const origin: V3 = [
        this.pos.x + vert.normal.x * 0.8,
        this.pos.y,
        this.pos.z + vert.normal.z * 0.8,
      ];
      const wallHit = this.world.raycast(origin, [-vert.normal.x, 0, -vert.normal.z], 2);
      if (wallHit && (wallHit.flags & SURFACE.transition) !== 0) {
        this.land(wallHit.point, wallHit.normal, wallHit.flags);
        return true;
      }
      this.vert = null;
    }
    return false;
  }

  private tryLand(): boolean {
    tmpA.subVectors(this.pos, this.prev);
    const dist = tmpA.length();
    let best: { point: V3; normal: V3; flags: number; t: number } | null = null;
    if (dist > 1e-6) {
      tmpA.divideScalar(dist);
      const hit = this.world.raycast(toV3(this.prev), toV3(tmpA), dist + 0.05);
      if (hit) {
        if (this.canLandOn(hit.normal, hit.flags)) best = hit;
        else {
          // Ceiling or wall hit mid-air: stop at the surface and kill inward velocity.
          fromV3(this.pos, hit.point).addScaledVector(tmpA, -0.05);
          fromV3(tmpB, hit.face);
          const into = this.vel.dot(tmpB);
          if (into < 0) this.vel.addScaledVector(tmpB, -into * 1.1);
        }
      }
    }
    if (!best && this.vel.y <= 0) {
      const down = this.world.raycast([this.pos.x, this.pos.y + 0.3, this.pos.z], [0, -1, 0], 0.32);
      if (down && this.canLandOn(down.normal, down.flags)) best = down;
    }
    if (!best) return false;
    this.land(best.point, best.normal, best.flags);
    return true;
  }

  private canLandOn(normal: V3, flags: number): boolean {
    const into = this.vel.x * normal[0] + this.vel.y * normal[1] + this.vel.z * normal[2];
    if (into > 0.5) return false;
    return (flags & SURFACE.transition) !== 0 || normal[1] > 0.6;
  }

  private land(point: V3, normal: V3, flags: number): void {
    fromV3(tmpB, normal);
    const impact = Math.max(0, -this.vel.dot(tmpB));
    this.vel.projectOnPlane(tmpB);
    fromV3(this.pos, point);

    // Compare the body heading with the travel direction on the landing surface.
    const bodyForward = tmpA.copy(this.fwd).projectOnPlane(tmpB);
    if (bodyForward.lengthSq() < 1e-4) bodyForward.copy(this.vel).projectOnPlane(tmpB);
    bodyForward.normalize();
    const travel = tmpC.copy(this.vel);
    const speed = travel.length();
    let angle = 0;
    if (speed > 1) {
      travel.divideScalar(speed);
      angle = (Math.acos(clamp(bodyForward.dot(travel), -1, 1)) * 180) / Math.PI;
    } else {
      travel.copy(bodyForward);
    }
    const tolerance = PHYSICS.landingToleranceDeg;
    const sideways = angle > tolerance && angle < 180 - tolerance;
    const unfinishedFlip = this.flip !== null && this.flip.t < PHYSICS.flipLandFraction;

    this.up.copy(tmpB);
    this.fwd.copy(travel).projectOnPlane(this.up).normalize();
    if (this.fwd.lengthSq() < 0.5) this.fwd.copy(bodyForward);
    this.groundFlags = flags;
    this.mode = 'ground';
    this.vert = null;
    this.coyote = 0;

    if (sideways || unfinishedFlip) {
      this.bail('landing', false);
      return;
    }
    if (this.flip) this.tricks.push(TRICKS[this.ride].flips[this.flip.variant] ?? 'Flip');
    if (this.grabVariant >= 0 && this.grabTime > 0.25) {
      this.tricks.push(TRICKS[this.ride].grabs[this.grabVariant] ?? 'Grab');
    }
    const spins = Math.round(this.spinTotal / Math.PI) * 180;
    const names = [...this.tricks];
    if (spins >= 180) names.unshift(String(spins));
    this.flip = null;
    this.grabVariant = -1;
    this.emit({ type: 'land', airTime: this.airTime, tricks: names, impact });
    // Hard flat landings scrub some speed.
    if (impact > 9) this.vel.multiplyScalar(0.85);
  }

  // ---------------------------------------------------------------- grind

  private tryRails(): boolean {
    if (this.vel.y > 2.5) return false;
    const pos = this.pos;
    const horizontalSpeed = Math.hypot(this.vel.x, this.vel.z);
    const hit = this.rails.nearest(toV3(pos), PHYSICS.grindSnapRadius, (candidate) => {
      const dy = candidate.point[1] - pos.y;
      if (dy < -PHYSICS.grindSnapBelow || dy > PHYSICS.grindSnapAbove) return false;
      if (candidate.rail.kind === 'coping' && this.vert) return false;
      if (candidate.rail === this.lastRail && this.railCooldown > 0) return false;
      if (horizontalSpeed < 1) return true;
      const tangent = railTangent(candidate.rail, candidate.seg);
      const th = Math.hypot(tangent[0], tangent[2]);
      if (th < 1e-3) return false;
      const alignment =
        Math.abs(tangent[0] * this.vel.x + tangent[2] * this.vel.z) / (th * horizontalSpeed);
      return alignment > PHYSICS.grindAlignDot;
    });
    if (!hit) return false;
    this.rail = hit.rail;
    this.railCursor.seg = hit.seg;
    this.railCursor.t = hit.t;
    const tangent = fromV3(tmpA, railTangent(hit.rail, hit.seg));
    let along = this.vel.dot(tangent);
    if (Math.abs(along) < 0.5) along = this.fwd.dot(tangent) >= 0 ? 1 : -1;
    this.railDir = along >= 0 ? 1 : -1;
    this.railSpeed = Math.max(PHYSICS.grindMinSpeed, Math.abs(along));
    this.balance = (this.random() - 0.5) * 0.2;
    this.balanceVel = (this.random() - 0.5) * 0.6;
    this.grindTime = 0;
    this.grindVariant = directionVariant(
      this.lastInput.steer,
      this.lastInput.push,
      this.lastInput.brake,
    );
    if (this.flip) this.tricks.push(TRICKS[this.ride].flips[this.flip.variant] ?? 'Flip');
    this.flip = null;
    this.grabVariant = -1;
    this.mode = 'grind';
    this.vert = null;
    this.placeOnRail();
    this.emit({ type: 'grindStart', kind: hit.rail.kind });
    return true;
  }

  private placeOnRail(): void {
    const rail = this.rail!;
    fromV3(this.pos, railPoint(rail, this.railCursor));
    this.pos.y += this.tuning.grindOffset;
    const tangent = fromV3(tmpA, railTangent(rail, this.railCursor.seg)).multiplyScalar(
      this.railDir,
    );
    this.vel.copy(tangent).multiplyScalar(this.railSpeed);
    this.up.copy(WORLD_UP);
    this.fwd.copy(tangent).projectOnPlane(this.up);
    if (this.fwd.lengthSq() < 1e-6) this.fwd.set(0, 0, 1);
    this.fwd.normalize();
  }

  private stepGrind(dt: number, input: SimInput): void {
    this.lastInput = input;
    const rail = this.rail!;
    this.grindTime += dt;
    const tangent = fromV3(tmpA, railTangent(rail, this.railCursor.seg)).multiplyScalar(
      this.railDir,
    );
    this.railSpeed += -PHYSICS.gravity * tangent.y * dt;
    this.railSpeed -= this.tuning.grindFriction * dt;
    if (input.flip && this.trickCooldown <= 0) {
      this.grindVariant = (this.grindVariant + 1) % 4;
      this.trickCooldown = 0.3;
    }

    const drift = this.tuning.balanceDrift;
    const noise =
      (this.random() * 2 - 1) *
      PHYSICS.balanceNoise *
      (1 + this.grindTime * PHYSICS.balanceNoiseGrowth);
    this.balanceVel += (noise + this.balance * PHYSICS.balanceGain) * drift * dt;
    this.balanceVel += input.steer * PHYSICS.balanceCorrection * dt;
    this.balanceVel *= Math.exp(-PHYSICS.balanceDamping * dt);
    this.balance += this.balanceVel * dt;

    if (this.jumpBuffer > 0) {
      this.jumpBuffer = 0;
      this.leaveRail();
      const side = tmpB.crossVectors(this.up, this.fwd).normalize();
      this.vel.addScaledVector(WORLD_UP, this.tuning.ollie * 0.95);
      this.vel.addScaledVector(side, -input.steer * PHYSICS.railHopLateral);
      this.emit({ type: 'ollie', charge: 0 });
      this.enterAir(false, true);
      return;
    }
    if (Math.abs(this.balance) >= 1) {
      const side = tmpB.crossVectors(this.up, this.fwd).normalize();
      this.vel.multiplyScalar(0.5).addScaledVector(side, -Math.sign(this.balance) * 2.5);
      this.bail('balance', false);
      return;
    }
    if (this.railSpeed < 1) {
      this.leaveRail();
      this.enterAir(false, true);
      return;
    }
    const leftover = advanceRail(rail, this.railCursor, this.railDir * this.railSpeed * dt);
    this.placeOnRail();
    if (leftover !== 0) {
      this.leaveRail();
      this.vel.y += PHYSICS.railExitLift;
      this.enterAir(false, true);
      this.pos.addScaledVector(this.vel, dt);
    }
  }

  private leaveRail(): void {
    this.lastRail = this.rail;
    this.railCooldown = 0.35;
    this.rail = null;
    this.emit({ type: 'grindEnd' });
    this.tricks.push(TRICKS[this.ride].grinds[this.grindVariant] ?? 'Grind');
  }

  // ---------------------------------------------------------------- bail

  private bail(reason: 'landing' | 'balance' | 'wall' | 'knockout', locked: boolean): void {
    if (this.mode === 'bail') {
      this.bailLocked = this.bailLocked || locked;
      return;
    }
    this.bailGrounded = this.mode === 'ground';
    if (this.mode === 'grind') this.leaveRail();
    this.tricks = [];
    this.mode = 'bail';
    this.bailTime = 0;
    this.bailLocked = locked;
    this.flip = null;
    this.grabVariant = -1;
    this.vert = null;
    this.charge = 0;
    this.tumble = 0;
    this.tumbleAxis.crossVectors(WORLD_UP, this.vel.lengthSq() > 0.1 ? this.vel : this.fwd);
    if (this.tumbleAxis.lengthSq() < 1e-4) this.tumbleAxis.set(1, 0, 0);
    this.tumbleAxis.normalize();
    this.emit({ type: 'bail', reason });
  }

  private bailGrounded = false;

  private stepBail(dt: number): void {
    this.bailTime += dt;
    const speed = this.vel.length();
    this.tumble += dt * clamp(speed * 1.4, 0, 14) * (this.bailGrounded ? 0.6 : 1);
    this.prev.copy(this.pos);
    if (this.bailGrounded) {
      const n = this.up;
      this.vel.addScaledVector(tmpB.set(0, -PHYSICS.gravity, 0).projectOnPlane(n), dt);
      this.decelerate(7 * dt);
      this.pos.addScaledVector(this.vel, dt);
      this.resolveWalls(true);
      const origin = tmpA.copy(this.pos).addScaledVector(n, 0.5);
      const hit = this.world.raycast(toV3(origin), [-n.x, -n.y, -n.z], 1.2);
      if (hit && (hit.normal[1] > 0.3 || (hit.flags & SURFACE.transition) !== 0)) {
        fromV3(this.pos, hit.point);
        fromV3(tmpB, hit.normal);
        rotateBetween(this.vel, n, tmpB);
        this.up.copy(tmpB);
        this.vel.projectOnPlane(this.up);
        this.groundFlags = hit.flags;
      } else {
        this.bailGrounded = false;
      }
    } else {
      this.vel.y -= PHYSICS.gravity * dt;
      this.pos.addScaledVector(this.vel, dt);
      tmpA.subVectors(this.pos, this.prev);
      const dist = tmpA.length();
      const hit =
        dist > 1e-6
          ? this.world.raycast(toV3(this.prev), toV3(tmpA.divideScalar(dist)), dist + 0.05)
          : null;
      if (hit && hit.normal[1] > -0.2) {
        fromV3(this.pos, hit.point);
        fromV3(tmpB, hit.normal);
        this.vel.projectOnPlane(tmpB).multiplyScalar(0.6);
        this.up.copy(tmpB);
        this.groundFlags = hit.flags;
        this.bailGrounded = hit.normal[1] > 0.3 || (hit.flags & SURFACE.transition) !== 0;
      } else {
        this.resolveWalls(false);
      }
    }
    if (
      !this.bailLocked &&
      this.bailTime > PHYSICS.bailSeconds &&
      this.bailGrounded &&
      this.vel.length() < 4
    ) {
      this.recover();
    }
  }

  private recover(): void {
    this.mode = 'ground';
    this.tumble = 0;
    this.bailGrounded = false;
    this.fwd.copy(this.vel).projectOnPlane(this.up);
    if (this.fwd.lengthSq() < 0.1) {
      this.fwd.copy(this.tumbleAxis).cross(this.up).projectOnPlane(this.up);
    }
    if (this.fwd.lengthSq() < 1e-6) this.fwd.set(0, 0, 1).projectOnPlane(this.up);
    this.fwd.normalize();
    this.emit({ type: 'recover' });
  }

  // ---------------------------------------------------------------- walls

  private resolveWalls(grounded: boolean): void {
    for (let iteration = 0; iteration < 2; iteration += 1) {
      let moved = false;
      for (const [height, radius] of [
        [PHYSICS.bodyHeight, PHYSICS.bodyRadius],
        [PHYSICS.footHeight, PHYSICS.footRadius],
      ] as const) {
        const up = grounded ? this.up : WORLD_UP;
        tmpC.copy(this.pos).addScaledVector(up, height);
        this.world.sphere(toV3(tmpC), radius, contacts);
        for (const contact of contacts) {
          const nx = contact.normal[0];
          const ny = contact.normal[1];
          const nz = contact.normal[2];
          if (grounded) {
            if (nx * this.up.x + ny * this.up.y + nz * this.up.z > 0.55) continue;
          } else if (ny > 0.6) {
            continue;
          }
          this.pos.x += nx * contact.depth;
          this.pos.y += ny * contact.depth;
          this.pos.z += nz * contact.depth;
          moved = true;
          const into = this.vel.x * nx + this.vel.y * ny + this.vel.z * nz;
          if (into < 0) {
            this.vel.x -= nx * into * 1.15;
            this.vel.y -= ny * into * 1.15;
            this.vel.z -= nz * into * 1.15;
            if (-into > 4) this.emit({ type: 'wallHit', speed: -into });
            if (grounded && this.mode === 'ground' && -into > PHYSICS.wallBailSpeed) {
              this.bail('wall', false);
              return;
            }
          }
        }
      }
      if (!moved) break;
    }
  }

  // ---------------------------------------------------------------- pose

  private updateQuaternion(): void {
    const up = this.up;
    const fwd = tmpA.copy(this.fwd).projectOnPlane(up);
    if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, 1).projectOnPlane(up);
    fwd.normalize();
    const right = tmpB.crossVectors(up, fwd).normalize();
    tmpM.makeBasis(right, up, fwd);
    this.quaternion.setFromRotationMatrix(tmpM);
    if (this.mode === 'bail' && this.tumble !== 0) {
      tmpQ.setFromAxisAngle(
        this.tumbleAxis,
        this.bailGrounded ? Math.min(this.tumble, Math.PI / 2) : this.tumble,
      );
      this.quaternion.premultiply(tmpQ);
    }
  }
}
